/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Tries to find element using closest selector.
 * 
 * @param {HTMLElement} fieldElement - Field element
 * @param {string} selector - CSS selector
 * @returns {HTMLElement | null} Found element or null
 */
function tryClosest(fieldElement: HTMLElement, selector: string): HTMLElement | null {
  return fieldElement.closest(selector);
}

/**
 * Tries to find element from label parent.
 * 
 * @param {HTMLElement} fieldElement - Field element
 * @returns {HTMLElement | null} Found element or null
 */
function tryLabelParent(fieldElement: HTMLElement): HTMLElement | null {
  const label = fieldElement.closest('label');
  return label?.parentElement || null;
}

/**
 * Tries to find element using multiple selectors in order.
 * 
 * @param {HTMLElement} fieldElement - Field element
 * @returns {HTMLElement | null} Found element or null
 */
function trySelectors(fieldElement: HTMLElement): HTMLElement | null {
  const selectors = ['[data-input-container]', '.mantine-Grid-col', '[class*="Grid"]'];
  for (const selector of selectors) {
    const found = tryClosest(fieldElement, selector);
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Finds element to highlight from field element using closest selectors.
 * 
 * @param {HTMLElement} fieldElement - Field element
 * @returns {HTMLElement | null} Element to highlight
 */
export function findElementFromField(fieldElement: HTMLElement): HTMLElement | null {
  const fromSelectors = trySelectors(fieldElement);
  if (fromSelectors) {
    return fromSelectors;
  }
  
  const fromLabel = tryLabelParent(fieldElement);
  if (fromLabel) {
    return fromLabel;
  }
  
  return fieldElement.parentElement || fieldElement;
}
